import { io, type Socket } from 'socket.io-client';
import type { ApiSurface } from '../auth/login.js';
import { getAccessToken } from '../auth/session.js';
import { getRealtimeHttpOrigin, getRealtimeUrl, isRealtimeEnabled } from '../env/realtime.js';
import { REALTIME_MAX_ROOMS_PER_CONNECTION, resolveRealtimeNamespace } from './catalog.js';
import type { RealtimeConnectOptions, RealtimeSubscribeResult } from './types.js';

type ConnectOptions = RealtimeConnectOptions & {
  url?: string;
  namespace?: string;
  surface?: ApiSurface;
};

const sockets = new Map<string, Socket>();
/** Origin → epoch ms until a new /live probe is allowed. */
const unavailableUntil = new Map<string, number>();
const warnedOrigins = new Set<string>();
const liveProbeInflight = new Map<string, Promise<boolean>>();

const PROBE_RETRY_MS = 20_000;
const RECONNECT_ATTEMPTS = 20;

function socketKey(url: string, namespace: string): string {
  return `${url}|${namespace}`;
}

function normalizeUrl(url: string): string {
  return url.replace(/^ws:/, 'http:').replace(/^wss:/, 'https:').replace(/\/$/, '');
}

function resolveNamespace(options: ConnectOptions): string {
  if (options.namespace) {
    return options.namespace.startsWith('/') ? options.namespace : `/${options.namespace}`;
  }
  if (options.surface) {
    return resolveRealtimeNamespace(options.surface);
  }
  return '/customer';
}

function buildAuth(options: RealtimeConnectOptions): Record<string, string> {
  const auth: Record<string, string> = {
    platform: options.platform ?? 'WEB',
  };
  if (options.clientVersion) {
    auth.clientVersion = options.clientVersion;
  }
  if (options.ticket) {
    auth.ticket = options.ticket;
  }
  if (options.accessToken) {
    auth.token = options.accessToken;
  }
  return auth;
}

function warnOnce(origin: string, message: string): void {
  if (warnedOrigins.has(origin)) return;
  warnedOrigins.add(origin);
  if (typeof console !== 'undefined') {
    console.warn(`[realtime] ${message}`);
  }
}

function isTemporarilyUnavailable(origin: string): boolean {
  const until = unavailableUntil.get(origin);
  if (!until) return false;
  if (Date.now() >= until) {
    unavailableUntil.delete(origin);
    warnedOrigins.delete(origin);
    return false;
  }
  return true;
}

function markTemporarilyUnavailable(url: string, reason: string): void {
  const origin = normalizeUrl(url);
  unavailableUntil.set(origin, Date.now() + PROBE_RETRY_MS);
  warnOnce(origin, `${reason} — retrying live updates shortly`);
}

function clearUnavailable(url: string): void {
  const origin = normalizeUrl(url);
  unavailableUntil.delete(origin);
  warnedOrigins.delete(origin);
}

/** Probe gateway `/live`. Failures are TTL-cached so a brief outage does not disable sockets for the session. */
export async function probeRealtimeGateway(url = getRealtimeUrl()): Promise<boolean> {
  if (!isRealtimeEnabled()) return false;
  const origin = getRealtimeHttpOrigin(url);
  if (isTemporarilyUnavailable(origin)) return false;

  const existing = liveProbeInflight.get(origin);
  if (existing) return existing;

  const probe = (async () => {
    try {
      const controller = typeof AbortController !== 'undefined' ? new AbortController() : null;
      const timer =
        controller && typeof setTimeout !== 'undefined'
          ? setTimeout(() => controller.abort(), 2500)
          : null;
      const res = await fetch(`${origin}/live`, {
        method: 'GET',
        signal: controller?.signal,
        credentials: 'omit',
      });
      if (timer) clearTimeout(timer);
      if (!res.ok) {
        markTemporarilyUnavailable(origin, `Gateway unavailable (${res.status}) at ${origin}/live`);
        return false;
      }
      clearUnavailable(origin);
      return true;
    } catch {
      markTemporarilyUnavailable(origin, `Gateway unreachable at ${origin}/live`);
      return false;
    } finally {
      liveProbeInflight.delete(origin);
    }
  })();

  liveProbeInflight.set(origin, probe);
  return probe;
}

/** Connect to a namespace on the realtime gateway — 10-11 § Handshake */
export function connectRealtime(
  urlOrOptions: string | ConnectOptions,
  legacyToken?: string,
): Socket | null {
  if (!isRealtimeEnabled()) return null;

  const options: ConnectOptions =
    typeof urlOrOptions === 'string'
      ? { url: urlOrOptions, accessToken: legacyToken }
      : urlOrOptions;

  const url = normalizeUrl(options.url ?? getRealtimeUrl());
  if (isTemporarilyUnavailable(url)) return null;

  const namespace = resolveNamespace(options);
  const key = socketKey(url, namespace);
  const existing = sockets.get(key);
  if (existing) {
    return existing;
  }

  const socket = io(`${url}${namespace}`, {
    auth: (cb: (data: Record<string, string>) => void) => {
      cb(
        buildAuth({
          ...options,
          accessToken: options.accessToken ?? getAccessToken() ?? undefined,
        }),
      );
    },
    transports: ['websocket', 'polling'],
    reconnection: true,
    reconnectionAttempts: RECONNECT_ATTEMPTS,
    reconnectionDelay: 1500,
    reconnectionDelayMax: 15_000,
  });

  socket.on('connect', () => {
    clearUnavailable(url);
  });

  socket.on('reconnect_failed', () => {
    markTemporarilyUnavailable(url, `Socket reconnect exhausted for ${url}${namespace}`);
    socket.disconnect();
    sockets.delete(key);
  });

  sockets.set(key, socket);
  return socket;
}

/**
 * Prefer this from UI hooks: probes `/live` first so a missing cPanel realtime
 * app does not open a failing WebSocket.
 */
export async function connectRealtimeWhenAvailable(
  urlOrOptions: string | ConnectOptions,
  legacyToken?: string,
): Promise<Socket | null> {
  const options: ConnectOptions =
    typeof urlOrOptions === 'string'
      ? { url: urlOrOptions, accessToken: legacyToken }
      : urlOrOptions;
  const url = options.url ?? getRealtimeUrl();
  const ok = await probeRealtimeGateway(url);
  if (!ok) return null;
  return connectRealtime(options, legacyToken);
}

/** Explicit room subscribe — 10-11 `emit subscribe { rooms[] }` */
export function subscribeRooms(
  socketInstance: Socket,
  rooms: string[],
): Promise<RealtimeSubscribeResult> {
  const uniqueRooms = [...new Set(rooms)].slice(0, REALTIME_MAX_ROOMS_PER_CONNECTION);
  if (uniqueRooms.length === 0) {
    return Promise.resolve({ rooms: [], rejected: [] });
  }

  return new Promise((resolve) => {
    let settled = false;
    const finish = (result: RealtimeSubscribeResult) => {
      if (settled) {
        return;
      }
      settled = true;
      socketInstance.off('subscribed', onSubscribed);
      resolve(result);
    };

    const onSubscribed = (result: RealtimeSubscribeResult) => finish(result);

    socketInstance.once('subscribed', onSubscribed);
    socketInstance.emit('subscribe', { rooms: uniqueRooms }, (result?: RealtimeSubscribeResult) => {
      if (result) {
        finish(result);
      }
    });
  });
}

/** @deprecated Use subscribeRooms with `{ rooms: [room] }` payload shape */
export function subscribeRoom(socketInstance: Socket, room: string): () => void {
  void subscribeRooms(socketInstance, [room]);
  return () => {
    socketInstance.emit('leave', room);
  };
}

export function disconnectRealtime(url?: string, namespace?: string): void {
  if (url && namespace) {
    const key = socketKey(normalizeUrl(url), namespace);
    const socket = sockets.get(key);
    socket?.disconnect();
    sockets.delete(key);
    return;
  }

  for (const socket of sockets.values()) {
    socket.disconnect();
  }
  sockets.clear();
}

export function getRealtimeSocket(url: string, namespace: string): Socket | undefined {
  return sockets.get(socketKey(normalizeUrl(url), namespace));
}

/** Test helper — clears unavailable cache between cases. */
export function resetRealtimeAvailabilityForTests(): void {
  unavailableUntil.clear();
  warnedOrigins.clear();
  liveProbeInflight.clear();
}
